import { useActionConfirmation } from '../components/ActionFeedbackProvider';
// React and hooks
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Icons
import { Trash, Home, Pencil, Plus, Search, Sun, Moon, Package } from 'lucide-react';

// Animation
import { motion } from 'framer-motion';

// Theme hook
import useTheme from '../hooks/useTheme';

// API client
import api from '../lib/api';

// Modals
import AddProductModal from '../components/modals/AddProductModal';

// i18n
import { useTranslation } from 'react-i18next';
import PageHeading from '../components/PageHeading';

const ProductScreen = () => {
  // State
  const [products, setProducts] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [busyAction, setBusyAction] = useState(false);
  const [error, setError] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedProduct, setSelectedProduct] = useState(null);

  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();

  const confirmAction = useActionConfirmation();

  // Base button style (now using dark: classes)
  const baseBtn =
    "inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold shadow transition duration-200 " +
    "bg-black text-white hover:bg-white hover:text-black hover:scale-105 disabled:opacity-60 " +
    "dark:bg-white dark:text-black dark:hover:bg-black dark:hover:text-white";

  const getId = (obj) => obj?.id;

  // Load products, brands, categories, suppliers on mount
  useEffect(() => {
    loadAll();
  }, []);

  // Normalize IDs
  const normalize = (arr) => arr.map((o) => ({ ...o, id: o.id || o._id }));

  // Load all data
  const loadAll = async () => {
    try {
      setLoading(true);
      setError('');
      const [pRes, bRes, cRes, sRes] = await Promise.all([
        api.get('/product-templates'),
        api.get('/brands'),
        api.get('/categories'),
        api.get('/suppliers'),
      ]);
      setProducts(normalize(pRes.data || []));
      setBrands(normalize(bRes.data || []));
      setCategories(normalize(cRes.data || []));
      setSuppliers(normalize(sRes.data || []));
    } catch (err) {
      setError(err?.response?.data?.message || t("loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  // Refresh only products
  const refreshProducts = async () => {
    try {
      const res = await api.get('/product-templates');
      setProducts(normalize(res.data || []));
    } catch (err) {
      setError(err?.response?.data?.message || t("refreshFailed"));
    }
  };

  // Delete product
  const handleDelete = async (id) => {
    if (!await confirmAction(t("confirmDeleteProduct"))) return;
    try {
      setBusyAction(true);
      setError('');
      await api.delete(`/product-templates/${id}`);
      await refreshProducts();
    } catch (err) {
      setError(err?.response?.data?.message || t("deleteFailed"));
    } finally {
      setBusyAction(false);
    }
  };

  // Helpers for names
  const brandName = (id) => brands.find((b) => getId(b) === id)?.name || '-';
  const categoryName = (id) => categories.find((c) => getId(c) === id)?.name || '-';
  const supplierName = (id, fallback) =>
    suppliers.find((s) => getId(s) === id)?.name || fallback || '-';

  // Open create modal
  const openCreate = () => {
    setModalMode('create');
    setSelectedProduct(null);
    setShowModal(true);
  };

  // Open edit modal
  const openEdit = (product) => {
    setModalMode('edit');
    setSelectedProduct(product);
    setShowModal(true);
  };

  // Search filter
  const filteredProducts = products.filter((p) => {
    const bName = brandName(p.brandId);
    const cName = categoryName(p.categoryId);
    const sName = supplierName(p.supplierId, p.supplier);
    const term = search.toLowerCase();
    return (
      p.name.toLowerCase().includes(term) ||
      bName.toLowerCase().includes(term) ||
      cName.toLowerCase().includes(term) ||
      sName.toLowerCase().includes(term)
    );
  });

  return (
    <motion.div
      className="min-h-full px-6 py-8 relative"
      initial={false}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
    >
      {/* Theme toggle */}
      <button
        onClick={toggleTheme}
        className="absolute top-4 right-4 z-10 text-black dark:text-white"
        title={t("toggleTheme")}
      >
        {theme === 'dark' ? <Sun size={22} /> : <Moon size={22} />}
      </button>

      {/* Card */}
      <div className="relative z-10 max-w-6xl mx-auto bg-white dark:bg-gray-800 shadow-xl rounded-xl p-6 backdrop-blur-md">
        {/* Header */}
        <PageHeading icon={<Package size={26} />} title={t("productTemplates")} />
        <div className="mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <label className="flex w-full items-center rounded-lg bg-gray-100 px-3 py-2 dark:bg-gray-700 md:w-64">
            <Search size={16} className="mr-2 text-gray-500 dark:text-gray-300" />
            <input
              type="search"
              aria-label={t('search')}
              placeholder={t('search')}
              className="w-full bg-transparent text-sm text-gray-700 outline-none dark:text-gray-200"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>

          <div className="flex flex-wrap gap-2 md:justify-end">
            <button onClick={openCreate} className={baseBtn} disabled={busyAction}>
              <Plus size={16} /> {t("addProductTemplate")}
            </button>
            <button onClick={() => navigate('/')} className={baseBtn}>
              <Home size={16} /> {t("home")}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 rounded-lg bg-red-600/90 text-white px-4 py-2">
            {error}
          </div>
        )}

        {/* Products list */}
        {loading ? (
          <div className="text-gray-700 dark:text-gray-200">{t("loading")}</div>
        ) : filteredProducts.length > 0 ? (
          <div className="space-y-2">
            {filteredProducts.map((p) => (
              <div
                key={getId(p)}
                className="flex items-center justify-between border border-gray-300 dark:border-gray-600 rounded-md p-3 bg-white dark:bg-gray-700 shadow-sm"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-800 dark:text-white truncate">
                    {p.name}
                  </p>
                  <p className="text-xs text-gray-600 dark:text-gray-300">
                    {t("brand")}: {brandName(p.brandId)} • {t("category")}: {categoryName(p.categoryId)} • {t("supplier")}: {supplierName(p.supplierId, p.supplier)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {p.hasBaseWeight
                      ? `${t("fixedWeight")}: ${p.fixedWeight || 0} kg`
                      : t("variableWeight")}
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    onClick={() => openEdit(p)}
                    className="p-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => handleDelete(getId(p))}
                    className="p-2 rounded-md bg-red-600 hover:bg-red-700 text-white"
                  >
                    <Trash size={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="border border-dashed border-gray-300 dark:border-gray-600 p-6 rounded-md text-gray-500 dark:text-gray-400 text-center">
            {t("noProductsFound")}
          </div>
        )}
      </div>

      {/* Add/Edit modal */}
      <AddProductModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSaved={refreshProducts}
        product={selectedProduct}
        mode={modalMode}
      />
    </motion.div>
  );
};

export default ProductScreen;
